import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import {
  RefreshCw, Coins, ArrowRightLeft, TrendingUp, Globe,
  AlertTriangle, Lightbulb, ShieldCheck, BarChart3,
} from 'lucide-react';
import { ExportDropdown } from '@/components/ExportDropdown';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import { FilterPanel } from '@/components/FilterPanel';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

const COLORS = [
  'hsl(222, 80%, 55%)', 'hsl(160, 70%, 45%)', 'hsl(45, 85%, 55%)',
  'hsl(340, 75%, 55%)', 'hsl(280, 65%, 55%)', 'hsl(200, 70%, 50%)',
  'hsl(30, 80%, 55%)', 'hsl(130, 60%, 45%)',
];

const INSIGHT_ICONS = {
  risk: AlertTriangle, optimization: TrendingUp,
  hedging: ShieldCheck, compliance: Globe,
};

// Shows the home-currency amount when viewing in another display currency
const domesticSubtitle = (data) => {
  const home = data.exposure?.domestic_currency || 'GBP';
  if (home === data.base_currency) return `${home} spend (home currency)`;
  const own = data.currencies?.find((c) => c.currency === home);
  const amount = own ? fmt(own.original_spend, CURRENCY_SYMBOLS[home] || own.symbol || '') : '';
  return `${amount} ${home} (home currency) · shown in ${data.base_currency}`.trim();
};

const fmt = (val, symbol) => {
  if (val == null) return `${symbol}0`;
  if (Math.abs(val) >= 1e6) return `${symbol}${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `${symbol}${(val / 1e3).toFixed(1)}K`;
  return `${symbol}${val.toFixed(0)}`;
};

export default function CurrencyAnalytics() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [baseCurrency, setBaseCurrency] = useState('GBP');
  const { filters, setFilters } = useGlobalFilters();

  const VISIBLE_FILTERS = ['category', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit'];

  const fetchData = useCallback(async (base, f) => {
    setLoading(true);
    setError(null);
    try {
      const params = { base_currency: base || baseCurrency };
      const activeFilters = f || filters;
      Object.entries(activeFilters).forEach(([k, v]) => {
        if (v && v !== 'all') params[k] = v;
      });
      const res = await api.get('/currency/analytics', { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load currency analytics');
    } finally {
      setLoading(false);
    }
  }, [baseCurrency, filters]);

  const fetchInsights = useCallback(async (base) => {
    setInsightsLoading(true);
    try {
      const res = await api.get('/currency/fx-insights', {
        params: { base_currency: base || baseCurrency },
      });
      setInsights(res.data.insights || []);
    } catch {
      toast.error('Failed to generate FX insights');
    } finally {
      setInsightsLoading(false);
    }
  }, [baseCurrency]);

  useEffect(() => { fetchData(baseCurrency, filters); }, []);

  const handleBaseCurrencyChange = (val) => {
    setBaseCurrency(val);
    fetchData(val, filters);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(baseCurrency, newFilters);
  };

  const exportCSV = () => {
    if (!data?.currencies) return;
    const rows = data.currencies.map(c => ({
      Currency: c.currency,
      'Original Spend': c.original_spend,
      [`Converted (${data.base_currency})`]: c.converted_spend,
      'Transactions': c.transaction_count,
      'Avg Transaction': c.avg_transaction,
      'Share (%)': c.percentage,
      'Exchange Rate': c.exchange_rate,
    }));
    downloadCSV(rows, 'currency_analytics');
  };

  const exportPDF = () => {
    if (!data) return;
    buildPDF([{
      title: 'Multi-Currency Spend Analytics',
      content: [
        `Base Currency: ${data.base_currency}`,
        `Total Spend: ${fmt(data.total_spend_base, data.base_symbol)}`,
        `Currencies: ${data.currencies?.length || 0}`,
        `FX Exposure: ${data.exposure?.foreign_percentage?.toFixed(1)}%`,
        '',
        ...data.currencies.map(c =>
          `${c.currency}: ${c.symbol}${c.original_spend.toLocaleString()} (${c.percentage}%)`
        ),
      ],
    }], 'Currency_Analytics');
  };

  const sym = data?.base_symbol || cs;

  if (error && !data) {
    return (
      <div className="p-6 text-center" data-testid="currency-error">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-destructive" />
        <h2 className="text-lg font-semibold mb-2">Error Loading Data</h2>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => fetchData(baseCurrency, filters)}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="currency-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="currency-page-title">
            Multi-Currency Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            FX exposure analysis and multi-currency spend breakdown
            {data?.filters_applied?.length > 0 && <span className="text-primary"> (filtered)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={baseCurrency} onValueChange={handleBaseCurrencyChange}>
            <SelectTrigger className="w-[150px] h-8" data-testid="base-currency-selector">
              <Coins className="w-4 h-4 mr-1 opacity-60" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['GBP', 'USD', 'EUR', 'INR', 'AUD', 'CAD', 'CHF', 'JPY', 'SGD', 'AED'].map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button variant="outline" size="sm" className="h-8" onClick={() => fetchData(baseCurrency, filters)} disabled={loading} data-testid="refresh-currency">
            <RefreshCw className={cn('w-4 h-4 mr-1', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        visibleFilters={VISIBLE_FILTERS}
        compact={true}
        pageName="currency"
      />

      {data?.filters_applied?.length > 0 && !loading && (
        <Badge variant="outline" className="text-xs" data-testid="currency-filter-context">
          Filtered by: {data.filter_context}
        </Badge>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="currency-loading">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="currency-kpis">
            <KPICard
              title="Total Spend"
              value={fmt(data.total_spend_base, sym)}
              subtitle={`In ${data.base_currency}`}
              icon={<Coins className="w-4 h-4" />}
              help="Total spend across every transaction, FX-normalised to your base currency using admin-managed exchange rates."
              testId="kpi-total-spend"
            />
            <KPICard
              title="Currencies"
              value={data.currencies?.length || 0}
              subtitle={`${data.exposure?.foreign_currency_count || 0} foreign`}
              icon={<Globe className="w-4 h-4" />}
              help="Distinct currencies invoiced in. A high number of long-tail currencies is a candidate for consolidation into fewer transaction currencies."
              testId="kpi-currency-count"
            />
            <KPICard
              title="FX Exposure"
              value={`${data.exposure?.foreign_percentage?.toFixed(1) || 0}%`}
              subtitle={fmt(data.exposure?.foreign_spend || 0, sym)}
              icon={<ArrowRightLeft className="w-4 h-4" />}
              accent={data.exposure?.foreign_percentage > 20 ? 'destructive' : undefined}
              help="Share of spend invoiced in a currency other than your base currency — this is what moves with FX rates. Above 20% typically warrants hedging consideration."
              testId="kpi-fx-exposure"
            />
            <KPICard
              title="Domestic Spend"
              value={fmt(data.exposure?.domestic_spend || 0, sym)}
              subtitle={domesticSubtitle(data)}
              icon={<ShieldCheck className="w-4 h-4" />}
              help="Spend invoiced in your base currency — not affected by FX movements."
              testId="kpi-domestic-spend"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Currency Distribution Donut */}
            <Card data-testid="currency-distribution-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Currency Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {data.currencies?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={data.currencies}
                        dataKey="converted_spend"
                        nameKey="currency"
                        cx="50%" cy="50%"
                        innerRadius={55} outerRadius={95}
                        paddingAngle={2}
                        label={({ currency, percentage }) => `${currency} ${percentage}%`}
                        labelLine={false}
                      >
                        {data.currencies.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val) => fmt(val, sym)}
                        contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No data</p>
                )}
              </CardContent>
            </Card>

            {/* Currency Breakdown Table */}
            <Card className="lg:col-span-2" data-testid="currency-breakdown-table">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Currency Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground text-left">
                        <th className="pb-2 font-medium">Currency</th>
                        <th className="pb-2 font-medium text-right">Original</th>
                        <th className="pb-2 font-medium text-right">In {data.base_currency}</th>
                        <th className="pb-2 font-medium text-right">Txns</th>
                        <th className="pb-2 font-medium text-right">Avg Txn</th>
                        <th className="pb-2 font-medium text-right">Share</th>
                        <th className="pb-2 font-medium text-right">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.currencies?.map((c, i) => (
                        <tr key={c.currency} className="border-b last:border-0" data-testid={`currency-row-${c.currency}`}>
                          <td className="py-2 font-medium flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            {c.symbol}{c.currency}
                          </td>
                          <td className="py-2 text-right">{c.original_spend.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="py-2 text-right font-medium">{sym}{c.converted_spend.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="py-2 text-right">{c.transaction_count.toLocaleString()}</td>
                          <td className="py-2 text-right">{c.symbol}{c.avg_transaction.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="py-2 text-right">
                            <Badge variant={c.percentage > 50 ? 'default' : 'outline'} className="text-xs">
                              {c.percentage}%
                            </Badge>
                          </td>
                          <td className="py-2 text-right text-muted-foreground text-xs">{c.exchange_rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend */}
          <Card data-testid="currency-trend-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 opacity-60" /> Monthly Spend by Currency
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.monthly_trends?.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={data.monthly_trends} barCategoryGap="15%">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => fmt(v, sym)} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(val, name) => [fmt(val, sym), name.replace('_converted', '')]}
                      contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                    />
                    <Legend />
                    {data.all_currencies?.map((ccy, i) => (
                      <Bar
                        key={ccy}
                        dataKey={`${ccy}_converted`}
                        name={ccy}
                        stackId="stack"
                        fill={COLORS[i % COLORS.length]}
                        radius={i === data.all_currencies.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-8">No trend data available</p>
              )}
            </CardContent>
          </Card>

          {/* Suppliers & Categories by Currency */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Suppliers per Foreign Currency */}
            <Card data-testid="suppliers-by-currency">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top Suppliers by Foreign Currency</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.suppliers_by_currency && Object.entries(data.suppliers_by_currency)
                  .filter(([ccy]) => ccy !== data.exposure?.domestic_currency)
                  .map(([ccy, suppliers]) => (
                    <div key={ccy}>
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                        <Badge variant="outline" className="text-xs">{ccy}</Badge>
                      </h4>
                      {suppliers.map((s, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                          <span className="truncate max-w-[200px]">{s.supplier}</span>
                          <span className="text-muted-foreground">
                            {CURRENCY_SYMBOLS[ccy] || ccy}{s.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <span className="text-xs ml-1">({s.count} txns)</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )) || <p className="text-muted-foreground text-sm">No foreign currency suppliers</p>}
                {data.suppliers_by_currency && Object.keys(data.suppliers_by_currency)
                  .filter(ccy => ccy !== data.exposure?.domestic_currency).length === 0 && (
                  <p className="text-muted-foreground text-sm text-center py-4">All spend is in domestic currency</p>
                )}
              </CardContent>
            </Card>

            {/* Categories per Foreign Currency */}
            <Card data-testid="categories-by-currency">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Categories by Foreign Currency</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.categories_by_currency && Object.entries(data.categories_by_currency)
                  .filter(([ccy]) => ccy !== data.exposure?.domestic_currency)
                  .map(([ccy, cats]) => (
                    <div key={ccy}>
                      <h4 className="text-sm font-semibold mb-2">
                        <Badge variant="outline" className="text-xs">{ccy}</Badge>
                      </h4>
                      {cats.map((c, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                          <span className="truncate max-w-[200px]">{c.category}</span>
                          <span className="text-muted-foreground">
                            {CURRENCY_SYMBOLS[ccy] || ccy}{c.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )) || <p className="text-muted-foreground text-sm">No foreign currency categories</p>}
                {data.categories_by_currency && Object.keys(data.categories_by_currency)
                  .filter(ccy => ccy !== data.exposure?.domestic_currency).length === 0 && (
                  <p className="text-muted-foreground text-sm text-center py-4">All spend is in domestic currency</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* AI FX Insights */}
          <Card data-testid="fx-insights-section">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" /> AI FX Insights
              </CardTitle>
              <Button
                variant="outline" size="sm"
                onClick={() => fetchInsights(baseCurrency)}
                disabled={insightsLoading}
                data-testid="generate-fx-insights"
              >
                {insightsLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Lightbulb className="w-4 h-4 mr-1" />}
                {insights ? 'Regenerate' : 'Generate Insights'}
              </Button>
            </CardHeader>
            <CardContent>
              {insightsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground mr-2" />
                  <span className="text-sm text-muted-foreground">Analyzing FX exposure...</span>
                </div>
              ) : insights?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {insights.map((insight, i) => {
                    const Icon = INSIGHT_ICONS[insight.type] || Lightbulb;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="p-4 rounded-lg border bg-card"
                        data-testid={`fx-insight-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            'p-2 rounded-md',
                            insight.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            insight.priority === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                            'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          )}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-sm font-semibold">{insight.title}</h4>
                              <Badge variant="outline" className="text-[10px]">{insight.priority}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">{insight.description}</p>
                            {insight.potential_impact && (
                              <p className="text-xs font-medium text-primary mt-2">{insight.potential_impact}</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Click "Generate Insights" for AI-powered FX exposure analysis
                </p>
              )}
            </CardContent>
          </Card>

          {/* Exchange Rate Note */}
          <p className="text-xs text-muted-foreground text-center">
            {data.exchange_rates_note}
          </p>
        </>
      ) : null}
      <AIPanel page="currency" filters={filters} />
    </div>
  );
}

const CURRENCY_SYMBOLS = {
  GBP: '£', USD: '$', EUR: '€', INR: '₹', JPY: '¥', PKR: 'Rs',
  CNY: '¥', CHF: 'Fr', AUD: 'A$', CAD: 'C$', SGD: 'S$',
  AED: 'د.إ', SAR: '﷼', BRL: 'R$', KRW: '₩', THB: '฿',
};

function KPICard({ title, value, subtitle, icon, accent, help, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            {title}
            {help ? <HelpBubble text={help} /> : null}
          </span>
          <span className="opacity-50">{icon}</span>
        </div>
        <div className={cn('text-xl font-bold', accent === 'destructive' && 'text-red-500')}>
          {value}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      </CardContent>
    </Card>
  );
}
